import moment from 'moment';
import { useWaypoint } from '../WaypointProvider.jsx';
import { WaypointLink, formatDays, formatSpan, formatDate, names } from './bits.jsx';

/* Is a batch finished, running, or still to start? Compared on dates
   alone, the same way the dashboard does it -- a batch with no end is
   treated as running rather than as finished, because an open-ended
   batch is the normal case for a long course. */
const stateOf = (b) => {
  const now = moment();
  const start = b?.startTime ? moment(b.startTime) : null;
  const end = b?.endTime ? moment(b.endTime) : null;
  if (end && end.isValid() && end.isBefore(now, 'day')) return 'done';
  if (start && start.isValid() && start.isAfter(now, 'day')) return 'upcoming';
  return 'live';
};

const STATE_LABEL = { live: 'Running', upcoming: 'Upcoming', done: 'Finished' };
const STATE_TONE = { live: 'pg-pill--good', upcoming: 'pg-pill--warn', done: '' };

/* One table for every batch-shaped tab. Batches, Running and Upcoming
   differ only in which rows they hold and what to say when there are
   none -- three copies of this markup would drift apart by the second
   change. */
const BatchTable = ({ rows, empty, onOpen, showState = false }) => {
  if (!rows || rows.length === 0) return <p className="lp-muted">{empty}</p>;

  return (
    <div className="lp-dialog-table">
      <table>
        <thead>
          <tr>
            <th>Sr. No</th>
            <th>Batch</th>
            {showState && <th>State</th>}
            <th>Mode</th>
            <th>Runs</th>
            <th>Days</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((b, i) => {
            const id = b?.id || b?._id;
            const state = stateOf(b);
            return (
              <tr key={id || `${b?.name}-${i}`}>
                <td className="lp-num">{i + 1}</td>
                <td className="lp-name">{b?.name || 'Unnamed batch'}</td>
                {showState && (
                  <td>
                    <span className={`pg-pill ${STATE_TONE[state]}`.trim()}>
                      {STATE_LABEL[state]}
                    </span>
                  </td>
                )}
                <td>{b?.mode || '—'}</td>
                <td>{formatSpan(b?.startTime, b?.endTime)}</td>
                <td>{formatDays(b?.days)}</td>
                <td className="wp-rowact">
                  {id && <WaypointLink onClick={() => onOpen(id)}>Open</WaypointLink>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default function StudentView({ entity, tab }) {
  const { push } = useWaypoint();
  const dash = entity?.dashboard || null;

  /* Batches come from the dashboard when it answered, and fall back to
     the ones populated on the student record when it did not -- the
     record's copy is thinner but it is still the right list. */
  const batches =
    (Array.isArray(dash?.batches) && dash.batches.length
      ? dash.batches
      : Array.isArray(entity?.batchIds)
        ? entity.batchIds
        : []) || [];

  const upcoming = Array.isArray(dash?.upcomingBatches) ? dash.upcomingBatches : [];
  const running = batches.filter((b) => stateOf(b) === 'live');
  const courses = Array.isArray(dash?.courses) ? dash.courses : [];

  const openBatch = (id) => push('batch', id);

  if (tab === 'batches') {
    return (
      <BatchTable
        rows={batches}
        empty="This student is not on any batch yet."
        onOpen={openBatch}
        showState
      />
    );
  }

  if (tab === 'running') {
    return (
      <BatchTable
        rows={running}
        empty="No batch is running for this student right now."
        onOpen={openBatch}
      />
    );
  }

  if (tab === 'upcoming') {
    return (
      <BatchTable
        rows={upcoming}
        empty="Nothing is scheduled for this student yet."
        onOpen={openBatch}
      />
    );
  }

  if (tab === 'courses') {
    if (courses.length === 0) {
      return <p className="lp-muted">This student is not enrolled on any course.</p>;
    }

    return (
      <div className="lp-dialog-table">
        <table>
          <thead>
            <tr>
              <th>Sr. No</th>
              <th>Course</th>
              <th>Progress</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {courses.map((c, i) => {
              // payload.courses is [{ course: {...}, progress }], not a
              // course array -- the progress belongs to the enrolment,
              // not to the course.
              const course = c?.course || c || {};
              const pct = Math.max(0, Math.min(100, Number(c?.progress) || 0));
              return (
                <tr key={course?.id || `${course?.name}-${i}`}>
                  <td className="lp-num">{i + 1}</td>
                  <td className="lp-name">{course?.name || 'Unnamed course'}</td>
                  <td>
                    <span className="wp-meter" aria-hidden="true">
                      <span className="wp-meter-fill" style={{ width: `${pct}%` }} />
                    </span>
                    <span className="wp-meter-n">{pct}%</span>
                  </td>
                  <td className="wp-rowact">
                    {course?.id && (
                      <WaypointLink onClick={() => push('course', course.id)}>
                        Open
                      </WaypointLink>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  if (tab === 'attendance') {
    const pct = Number(dash?.attendancePercentage) || 0;
    const attended = dash?.classesAttended ?? 0;

    return (
      <>
        <dl className="quiz-facts">
          <div>
            <dt>Attendance</dt>
            <dd>{pct}%</dd>
          </div>
          <div>
            <dt>Classes attended</dt>
            <dd>{attended}</dd>
          </div>
          <div>
            <dt>Batches</dt>
            <dd>{batches.length}</dd>
          </div>
        </dl>

        {batches.length === 0 ? (
          <p className="lp-muted">
            There is no attendance to show until this student is on a batch.
          </p>
        ) : (
          <>
            <h5 className="pgm-section-title">Per batch</h5>
            <div className="lp-dialog-table">
              <table>
                <thead>
                  <tr>
                    <th>Sr. No</th>
                    <th>Batch</th>
                    <th>Attended</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b, i) => {
                    const id = b?.id || b?._id;
                    return (
                      <tr key={id || `${b?.name}-${i}`}>
                        <td className="lp-num">{i + 1}</td>
                        <td className="lp-name">{b?.name || 'Unnamed batch'}</td>
                        {/* The count the dashboard carries per batch.
                            An em-dash rather than 0 when it is absent:
                            "not reported" and "attended none" are not
                            the same thing to read. */}
                        <td>{b?.attendanceCount ?? '—'}</td>
                        <td className="wp-rowact">
                          {id && (
                            <WaypointLink onClick={() => openBatch(id)}>Open</WaypointLink>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </>
    );
  }

  return (
    <dl className="quiz-facts">
      <div>
        <dt>Verified</dt>
        <dd>
          <span
            className={`pg-pill ${
              entity?.isVerifiedbyCounsellor ? 'pg-pill--good' : 'pg-pill--warn'
            }`}
          >
            {entity?.isVerifiedbyCounsellor ? 'Verified' : 'Unverified'}
          </span>
        </dd>
      </div>
      <div>
        <dt>Email</dt>
        <dd>{entity?.email || '—'}</dd>
      </div>
      <div>
        <dt>Phone</dt>
        <dd>{entity?.phone || '—'}</dd>
      </div>
      <div>
        <dt>Course</dt>
        <dd>{names(entity?.courseIds)}</dd>
      </div>
      <div>
        <dt>Department</dt>
        <dd>{names(entity?.departmentIds)}</dd>
      </div>
      <div>
        <dt>Preferred branch</dt>
        <dd>{names(entity?.preferredBranchId)}</dd>
      </div>
      <div>
        <dt>Mode of class</dt>
        <dd>{entity?.modeOfClass || '—'}</dd>
      </div>
      <div>
        <dt>Contact person</dt>
        <dd>{names(entity?.contactPersonIds)}</dd>
      </div>
      <div>
        <dt>Added</dt>
        <dd>{formatDate(entity?.createdAt)}</dd>
      </div>
    </dl>
  );
}
